<template>
  <div class="participant-detail">
    <router-link :to="{ name: 'ResultsDashboard' }" class="back-link">&larr; Kembali ke Daftar Peserta</router-link>

    <div v-if="isLoading" class="loading-overlay">
      <div class="spinner"></div>
      <p>Memuat data peserta...</p>
    </div>

    <div v-else-if="participant">
      <!-- 1. HEADER AKSI -->
      <header class="page-header card">
        <div class="title-section">
          <h1>Laporan: {{ participant.name }}</h1>
          <div class="meta-badges">
            <span class="badge-pkg">{{ participant.package_name }}</span>
            <span v-if="report && report.result_type === 'manual'" class="badge-vip">JALUR VIP</span>
          </div>
        </div>
        <div class="header-actions-container" style="display: flex; flex-direction: column; gap: 10px; align-items: flex-end;">
          <div class="header-actions">
            <!-- Tombol Generate Psikogram Paket 3 -->
            <button @click="downloadPsikogramPaket3" class="btn-action primary" style="margin-right: 10px;">
              <i class="fas fa-file-excel"></i> Psikogram Paket 3
            </button>
            <button @click="generateReport" :disabled="isProcessing" class="btn-action primary">
               {{ isProcessing ? '...' : '1. Hitung Skor Otomatis' }}
            </button>
            <button @click="saveFullReport" :disabled="!report || isProcessing" class="btn-action success">
               2. Simpan Draft Narasi
            </button>
            <button @click="generateProfessionalPdf" :disabled="!report" class="btn-action danger">
               3. Cetak PDF Laporan
            </button>
          </div>
          
          <div v-if="report" class="pdf-options" style="background: #f8f9fa; padding: 10px 15px; border-radius: 6px; border: 1px solid #e9ecef; font-size: 0.9em; display: flex; gap: 15px; align-items: center; flex-wrap: wrap;">
            <strong>Opsi Cetak:</strong>
            <label style="cursor: pointer; display: flex; align-items: center; gap: 5px;">
              <input type="checkbox" v-model="pdfOptions.showIQ"> Potensi (IQ)
            </label>
            <label style="cursor: pointer; display: flex; align-items: center; gap: 5px;">
              <input type="checkbox" v-model="pdfOptions.showPsychogram"> Psikogram
            </label>
            <label style="cursor: pointer; display: flex; align-items: center; gap: 5px;">
              <input type="checkbox" v-model="pdfOptions.showDISC"> DISC
            </label>
            <label style="cursor: pointer; display: flex; align-items: center; gap: 5px;">
              <input type="checkbox" v-model="pdfOptions.showPAPI"> PAPI Kostick
            </label>
            <label style="cursor: pointer; display: flex; align-items: center; gap: 5px;">
              <input type="checkbox" v-model="pdfOptions.showMBTI"> MBTI (Preferensi)
            </label>
            <label style="cursor: pointer; display: flex; align-items: center; gap: 5px;">
              <input type="checkbox" v-model="pdfOptions.showSDS"> Minat Karir (SDS)
            </label>
            <label style="cursor: pointer; display: flex; align-items: center; gap: 5px;">
              <input type="checkbox" v-model="pdfOptions.showConclusion"> Kesimpulan
            </label>
          </div>
        </div>
      </header>

      <!-- 2. INFORMASI IDENTITAS -->
      <div class="details-grid card">
        <div class="detail-item"><strong>Nomor Registrasi</strong><span>{{ participant.test_number }}</span></div>
        <div class="detail-item"><strong>Tanggal Lahir</strong><span>{{ formatDate(participant.birth_date, true) }}</span></div>
        <div class="detail-item"><strong>Pekerjaan</strong><span>{{ participant.job || '-' }}</span></div>
        <div class="detail-item"><strong>Tanggal Tes</strong><span>{{ formatDate(participant.created_at) }}</span></div>
        <div class="detail-item full"><strong>Posisi Dituju</strong><input v-model="reportFields.target_position" placeholder="Contoh: Tenaga Layanan - Psikolog Klinis"></div>
      </div>


    <!-- Sisipkan di bawah 'details-grid card' dan di ATAS 'report-editor' -->

    <div class="raw-data-section card">
      <div class="section-title">
        <i class="fas fa-microscope"></i>
        <h2>Verifikasi Data Mentah & Skor</h2>
      </div>
      <p class="hint">Periksa jawaban mentah peserta dan pastikan skor sudah sesuai sebelum men-generate laporan narasi.</p>
      
      <div class="table-container">
        <table class="verification-table">
          <thead>
            <tr>
              <th>Nama Tes</th>
              <th>Status</th>
              <th>Skor Saat Ini</th>
              <th class="text-center">Data Mentah</th>
              <th class="text-center">Aksi</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="session in participant.sessions" :key="session.id">
              <td><strong>{{ session.name }}</strong></td>
              <td>
                <span class="status-badge" :class="session.status">
                  {{ formatStatus(session.status) }}
                </span>
              </td>
              <td>
                <div class="score-edit-wrapper">
                  <input 
                    type="number" 
                    v-model.number="session.score" 
                    class="score-input"
                    @change="markAsChanged(session.id)"
                  >
                  <button 
                    v-if="changedScores.includes(session.id)" 
                    @click="updateManualScore(session)"
                    class="btn-save-mini"
                  >
                    Update
                  </button>
                </div>
              </td>
              <td class="text-center">
                <!-- Tombol Download Excel Per Tes -->
                 <!-- Gunakan session.id, karena di schema TestStatus, 'id' adalah ID Master Tes -->
              <button
                v-if="session.status === 'completed'"
                @click="downloadAnswersPerTest(session.id, session.name)"
                :disabled="isDownloading === session.id"
                class="btn-download-excel"
              >
                <i class="fas fa-file-excel"></i> Excel
              </button>
              <button
                v-if="session.status === 'completed'"
                @click="goToEssayCorrection(session.id)"
                class="btn-koreksi-essay"
                style="margin-left: 5px;"
              >
                <i class="fas fa-edit"></i> Koreksi Essay
              </button>
                <span v-else class="text-muted">-</span>
              </td>
              <td class="text-center">
                <button class="btn-text-only">Lihat Detail</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
      <!-- 3. EDITOR NARASI (Hanya muncul jika report sudah ada) -->
      <div v-if="report" class="report-editor">
        
        <!-- BAGIAN A: IQ -->
        <section class="editor-section card">
          <div class="section-title">
            <i class="fas fa-brain"></i>
            <h2>A. PROFIL POTENSI INDIVIDU (IQ)</h2>
          </div>
          <div class="iq-config-row">
             <div class="input-group">
               <label>Skor IQ</label>
               <input type="number" v-model.number="reportFields.iq_score">
             </div>
             <div class="input-group">
               <label>Kategori IQ</label>
               <input v-model="reportFields.iq_category" placeholder="e.g. High Average">
             </div>
          </div>
          <label class="label-block">Analisis Naratif Potensi IQ:</label>
          <textarea v-model="reportFields.iq_narrative" rows="5"></textarea>
        </section>

        <!-- BAGIAN B: PSIKOGRAM PREVIEW -->
        <section class="editor-section card">
          <div class="section-title">
            <i class="fas fa-table"></i>
            <h2>B. ASPEK PSIKOLOGIS (PSIKOGRAM)</h2>
          </div>
          <div class="table-container">
            <table class="preview-table">
              <thead>
                <tr><th>Aspek</th><th>Sub-Aspek</th><th>Kategori</th></tr>
              </thead>
              <tbody>
                <template v-for="aspect in parsedSummary.aspects" :key="aspect.name">
                  <tr v-for="(sub, idx) in aspect.sub_aspects" :key="sub.name">
                    <td v-if="idx === 0" :rowspan="aspect.sub_aspects.length" class="bg-light"><strong>{{ aspect.name }}</strong></td>
                    <td>{{ sub.name }}</td>
                    <td class="text-center">
                      <select v-model="sub.category" class="select-cat">
                        <option value="1">1</option><option value="2">2</option>
                        <option value="3">3</option><option value="4">4</option>
                        <option value="5">5</option>
                        <option value="R">R</option><option value="K">K</option>
                        <option value="C">C</option><option value="B">B</option>
                        <option value="T">T</option>
                      </select>
                    </td>
                  </tr>
                </template>
              </tbody>
            </table>
          </div>
        </section>

        <!-- BAGIAN C: PROFIL DISC -->
        <section v-if="reportFields.disc_data" class="editor-section card">
          <div class="section-title">
            <i class="fas fa-chart-bar"></i>
            <h2>C. PROFIL KEPRIBADIAN (DISC)</h2>
          </div>
          <div class="disc-tables-grid">
            <div class="disc-table">
              <h4 class="text-center">Graph I (Public)</h4>
              <table class="preview-table text-center">
                <thead>
                  <tr><th>D</th><th>I</th><th>S</th><th>C</th><th>*</th></tr>
                </thead>
                <tbody>
                  <tr>
                    <td>{{ reportFields.disc_data.graph_1_most['D'] }}</td>
                    <td>{{ reportFields.disc_data.graph_1_most['I'] }}</td>
                    <td>{{ reportFields.disc_data.graph_1_most['S'] }}</td>
                    <td>{{ reportFields.disc_data.graph_1_most['C'] }}</td>
                    <td>{{ reportFields.disc_data.graph_1_most['*'] }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div class="disc-table">
              <h4 class="text-center">Graph II (Private)</h4>
              <table class="preview-table text-center">
                <thead>
                  <tr><th>D</th><th>I</th><th>S</th><th>C</th><th>*</th></tr>
                </thead>
                <tbody>
                  <tr>
                    <td>{{ reportFields.disc_data.graph_2_least['D'] }}</td>
                    <td>{{ reportFields.disc_data.graph_2_least['I'] }}</td>
                    <td>{{ reportFields.disc_data.graph_2_least['S'] }}</td>
                    <td>{{ reportFields.disc_data.graph_2_least['C'] }}</td>
                    <td>{{ reportFields.disc_data.graph_2_least['*'] }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div class="disc-table">
              <h4 class="text-center">Graph III (Mirror)</h4>
              <table class="preview-table text-center">
                <thead>
                  <tr><th>D</th><th>I</th><th>S</th><th>C</th><th>*</th></tr>
                </thead>
                <tbody>
                  <tr>
                    <td>{{ reportFields.disc_data.graph_3_change['D'] }}</td>
                    <td>{{ reportFields.disc_data.graph_3_change['I'] }}</td>
                    <td>{{ reportFields.disc_data.graph_3_change['S'] }}</td>
                    <td>{{ reportFields.disc_data.graph_3_change['C'] }}</td>
                    <td>{{ reportFields.disc_data.graph_3_change['*'] }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>

        <!-- BAGIAN C1: PAPI KOSTICK -->
        <section v-if="reportFields.papi_data" class="editor-section card">
          <div class="section-title">
            <i class="fas fa-chart-pie"></i>
            <h2>PAPI KOSTICK</h2>
          </div>
          <div class="table-container">
            <table class="preview-table text-center" style="width: 100%; margin-top: 10px;">
              <thead>
                <tr>
                  <th v-for="key in Object.keys(reportFields.papi_data).sort()" :key="key">{{ key }}</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td v-for="key in Object.keys(reportFields.papi_data).sort()" :key="key">{{ reportFields.papi_data[key] }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <!-- BAGIAN C2: MINAT KARIR (SDS) -->
        <section v-if="reportFields.sds_data" class="editor-section card">
          <div class="section-title">
            <i class="fas fa-briefcase"></i>
            <h2>MINAT KARIR (SDS)</h2>
          </div>
          
          <div class="mb-4">
             <h4 class="text-center">Top 3 Minat Karir Berdasarkan Tes</h4>
             <table class="preview-table text-center" style="width: 100%; margin-top: 10px;">
               <thead>
                 <tr><th>Peringkat</th><th>Kategori</th><th>Total Skor</th></tr>
               </thead>
               <tbody>
                 <tr v-for="(item, idx) in getTop3Sds(reportFields.sds_data)" :key="idx">
                   <td>{{ idx + 1 }}</td>
                   <td>{{ item.name }}</td>
                   <td>{{ item.score }}</td>
                 </tr>
               </tbody>
             </table>
          </div>
          
          <div class="form-group mb-3">
            <label class="label-block">Narasi Minat Karir (Opsional):</label>
            <textarea v-model="reportFields.career_interests" rows="4" placeholder="Kosongkan jika ingin menggunakan tabel otomatis di atas pada PDF..."></textarea>
          </div>
        </section>

        <!-- BAGIAN D: KEPRIBADIAN -->
        <section class="editor-section card">
          <div class="section-title">
            <i class="fas fa-user-tie"></i>
            <h2>D. PREFERENSI KEPRIBADIAN</h2>
          </div>
          <div class="form-group mb-3">
            <label>Tipe Kepribadian (e.g. ISFJ / ENFP)</label>
            <input v-model="reportFields.personality_type" class="input-full">
          </div>
          <label class="label-block">Narasi Kepribadian:</label>
          <textarea v-model="reportFields.personality_narrative" rows="6"></textarea>
        </section>

        <!-- BAGIAN E: KESIMPULAN -->
        <section class="editor-section card">
          <div class="section-title">
            <i class="fas fa-check-circle"></i>
            <h2>E. KESIMPULAN & REKOMENDASI</h2>
          </div>
          <div class="grid-2">
            <div class="form-group">
              <label class="label-block">Area Kekuatan:</label>
              <textarea v-model="reportFields.strengths_list" rows="5"></textarea>
            </div>
            <div class="form-group">
              <label class="label-block">Area Pengembangan:</label>
              <textarea v-model="reportFields.weaknesses_list" rows="5"></textarea>
            </div>
          </div>

          <div class="recommendation-grid mt-4">
             <div class="form-group">
                <label>Presentase Kesesuaian (%)</label>
                <input v-model="reportFields.overall_score" placeholder="e.g. 92%">
             </div>
             <div class="form-group">
                <label>Hasil Rekomendasi</label>
                <select v-model="reportFields.conclusion" class="input-full">
                  <option value="Disarankan">Disarankan</option>
                  <option value="Dipertimbangkan">Dipertimbangkan</option>
                  <option value="Tidak Disarankan">Tidak Disarankan</option>
                </select>
             </div>
          </div>

          <div class="form-group mt-3">
            <label class="label-block">Saran Pengembangan:</label>
            <textarea v-model="reportFields.development_suggestions" rows="4"></textarea>
          </div>
        </section>

      </div>
    </div>
    <p v-if="error" class="error-text">{{ error }}</p>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import apiClient from '@/services/api.js';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import qr from 'qrcode';
const isDownloading = ref(null); 
// Assets
import logoImage from '@/assets/logo1.png';

const route = useRoute();
const router = useRouter();
const participantId = route.params.id;

// States
const participant = ref(null);
const report = ref(null);
const adminProfile = ref(null);
const isLoading = ref(true);
const isProcessing = ref(false);

const pdfOptions = ref({
  showIQ: true,
  showPsychogram: true,
  showDISC: true,
  showPAPI: true,
  showMBTI: true,
  showSDS: true,
  showConclusion: true
});

// Form Fields
const reportFields = ref({
  target_position: '',
  iq_score: 100,
  iq_category: '',
  iq_narrative: '',
  personality_type: '',
  personality_narrative: '',
  strengths_list: '',
  weaknesses_list: '',
  overall_score: '',
  conclusion: 'Dipertimbangkan',
  development_suggestions: '',
  aspects_data: [], // Untuk menyimpan edit kategori R-T
  disc_data: null,
  papi_data: null,
  sds_data: null
});
// --- Tambahkan State untuk melacak perubahan skor manual ---
const changedScores = ref([]);

const goToEssayCorrection = (testId) => {
  router.push(`/admin/participant/${route.params.id}/session/${testId}/essay`);
};

// --- Fungsi untuk memformat status teks (UNTUK MEMPERBAIKI ERROR) ---
const formatStatus = (status) => {
  if (status === 'not_started') return 'Belum Dikerjakan';
  if (status === 'in_progress') return 'Sedang Dikerjakan';
  if (status === 'completed') return 'Selesai';
  return status;
};

// --- Fungsi untuk menandai jika ada input skor yang diubah ---
const markAsChanged = (sessionId) => {
  if (!changedScores.value.includes(sessionId)) {
    changedScores.value.push(sessionId);
  }
};

// --- Fungsi untuk menyimpan skor hasil koreksi manual ke Database ---
const updateManualScore = async (session) => {
  try {
    // Memanggil API yang sudah kita buat di backend (Fase 20)
    await apiClient.put(`/admin/sessions/${session.id}/manual-score`, {
      score: session.score
    });
    
    // Hapus ID dari daftar "changed" setelah berhasil simpan
    changedScores.value = changedScores.value.filter(id => id !== session.id);
    
    alert(`Skor untuk ${session.name} berhasil diperbarui.`);
  } catch (err) {
    console.error(err);
    alert("Gagal memperbarui skor. Pastikan backend sudah didukung manual-score.");
  }
};

// --- Fungsi untuk Mendownload Jawaban Per Tes (Excel) ---
const downloadAnswersPerTest = async (testId, testName) => {
  if (!testId) {
    alert("ID Tes tidak valid");
    return;
  }
  isDownloading.value = testId;
  try {
    const response = await apiClient.get(
      `/admin/participants/${participantId}/tests/${testId}/download`,
      { responseType: 'blob' }
    );

    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `Jawaban_${testName}_${participant.value.name}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (err) {
    console.error(err);
    alert("Gagal download excel");
  } finally {
    isDownloading.value = null;
  }
};


const fetchData = async () => {
  isLoading.value = true;
  try {
    const [pRes, rRes, aRes] = await Promise.all([
      apiClient.get(`/admin/participants/${participantId}`),
      apiClient.get(`/admin/participants/${participantId}/report`).catch(() => ({ data: null })),
      apiClient.get('/admin/me').catch(() => ({ data: null }))
    ]);
    participant.value = pRes.data;
    adminProfile.value = aRes.data;
    if (rRes.data) {
      report.value = rRes.data;
      fillFields(rRes.data);
    }
  } finally {
    isLoading.value = false;
  }
};

const fillFields = (data) => {
  const summary = data.interpretation_summary ? JSON.parse(data.interpretation_summary) : {};
  reportFields.value = {
    ...reportFields.value,
    target_position: data.target_position || "",
    iq_score: data.iq_score || 0,
    iq_category: data.iq_category || "",
    iq_narrative: data.iq_narrative || "",
    personality_type: data.personality_type || "",
    personality_narrative: data.personality_narrative || "",
    strengths_list: data.strengths_list || "",
    weaknesses_list: data.weaknesses_list || "",
    overall_score: data.overall_score || "",
    conclusion: data.conclusion || "",
    development_suggestions: data.development_suggestions || "",
    aspects_data: summary.aspects || [],
    disc_data: summary.disc_data || null,
    papi_data: summary.papi_data || null,
    sds_data: summary.sds_data || null
  };
};

const parsedSummary = computed(() => {
  return { 
    aspects: reportFields.value.aspects_data,
    disc_data: reportFields.value.disc_data,
    papi_data: reportFields.value.papi_data,
    sds_data: reportFields.value.sds_data
  };
});

const getTop3Sds = (sdsScores) => {
  if (!sdsScores) return [];
  const sdsCategories = {
    'R': 'Realistic', 'I': 'Investigative',
    'A': 'Artistic', 'S': 'Social',
    'E': 'Enterprising', 'C': 'Conventional'
  };
  return Object.keys(sdsScores)
    .filter(k => sdsCategories[k])
    .map(k => ({ code: k, name: sdsCategories[k], score: sdsScores[k] }))
    .sort((a, b) => b.score - a.score)
    .slice(0, 3);
};

const generateReport = async () => {
  if (!confirm("Hitung ulang skor akan menimpa narasi yang sedang diedit. Lanjutkan?")) return;
  isProcessing.value = true;
  try {
    const res = await apiClient.post(`/admin/participants/${participantId}/generate-report`);
    report.value = res.data;
    fillFields(res.data);
  } catch (err) {
    alert("Error dari server: " + (err.response?.data?.detail || err.message));
    throw err;
  } finally { isProcessing.value = false; }
};

const downloadPsikogramPaket3 = async () => {
  try {
    const response = await apiClient.get(`/admin/participants/${participantId}/export-psikogram-paket3`, {
      responseType: 'blob' 
    });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `Psikogram_${participant.value.name}.xlsx`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } catch (error) {
    alert("Gagal mendownload Psikogram Paket 3. Pastikan template sudah tersedia dan peserta memiliki hasil.");
  }
};

const saveFullReport = async () => {
  isProcessing.value = true;
  try {
    // Pack kembali aspek data ke string JSON sebelum kirim
    const payload = {
      ...reportFields.value,
      interpretation_summary: JSON.stringify({ 
        aspects: reportFields.value.aspects_data,
        disc_data: reportFields.value.disc_data,
        papi_data: reportFields.value.papi_data,
        sds_data: reportFields.value.sds_data 
      })
    };
    const res = await apiClient.put(`/admin/results/${report.value.id}/full-update`, payload);
    report.value = res.data;
    alert("Draft Laporan Berhasil Disimpan!");
  } finally { isProcessing.value = false; }
};

// Helper untuk load gambar dari URL menjadi HTMLImageElement agar aman di jsPDF
const loadImageElement = (src) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'Anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Gagal memuat gambar: ${src}`));
    img.src = src;
  });
};

const calculateAge = (birthDateString) => {
  if (!birthDateString) return '-';
  const today = new Date();
  const birthDate = new Date(birthDateString);
  let age = today.getFullYear() - birthDate.getFullYear();
  const m = today.getMonth() - birthDate.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
  }
  return age + " Tahun";
};

const generateProfessionalPdf = async () => {
  try {
    const doc = new jsPDF({ orientation: 'p', unit: 'mm', format: [210, 297] });
    const p = participant.value;
    const rf = reportFields.value;
    const a = adminProfile.value || {};

    // Load aset gambar terlebih dahulu menjadi Image element
    const logoImgElement = await loadImageElement(logoImage);

    let y = 45;
    const checkPageBreak = (neededSpace) => {
      if (y + neededSpace > 275) {
        doc.addPage();
        y = 35;
        return true;
      }
      return false;
    };

    let sectionCharCode = 65; // 'A'
    const getLetter = () => String.fromCharCode(sectionCharCode++) + ". ";
    const opts = pdfOptions.value;
    
    // Title
    doc.setFontSize(14).setFont('helvetica', 'bold').text("LAPORAN ASESMEN PSIKOLOGIS", 105, y, { align: 'center' });
    y += 15;
    
    // Biodata
    doc.setFontSize(11).setFont('helvetica', 'normal');
    const rowHeader = (label, val, yPos) => { 
        doc.setFont('helvetica', 'bold');
        doc.text(label, 20, yPos); 
        doc.setFont('helvetica', 'normal');
        doc.text(`:     ${val}`, 70, yPos); 
    };
    
    rowHeader("Nama", p.name, y); y += 6;
    const genderStr = p.gender === 'L' ? 'Laki-laki' : (p.gender === 'P' ? 'Perempuan' : '-');
    rowHeader("Jenis Kelamin", genderStr, y); y += 6;
    rowHeader("Tempat, Tanggal Lahir", `${p.place_of_birth || '-'}, ${formatDate(p.birth_date, true)}`, y); y += 6;
    rowHeader("Tanggal Pemeriksaan", formatDate(new Date(), true), y); y += 6;
    rowHeader("Usia", calculateAge(p.birth_date), y); y += 6;
    rowHeader("Posisi yang Dituju", rf.target_position || '-', y); y += 12;

    // --- BAGIAN: PROFIL POTENSI (IQ) ---
    if (opts.showIQ) {
      checkPageBreak(30);
      doc.setFontSize(11).setFont('helvetica', 'bold').text(getLetter() + "PROFIL POTENSI INDIVIDU", 20, y);
      y += 5;
      
      autoTable(doc, {
        startY: y,
        head: [[
          { content: 'KAPASITAS IQ', styles: { halign: 'center' } }, 
          { content: rf.iq_score, styles: { halign: 'center' } }, 
          { content: '/', styles: { halign: 'center' } }, 
          { content: (rf.iq_category || '').toUpperCase(), styles: { halign: 'center' } }
        ]],
        body: [[{ content: rf.iq_narrative, colSpan: 4, styles: { fillColor: [255, 255, 255], textColor: [0,0,0], fontStyle: 'normal' } }]],
        theme: 'grid',
        headStyles: { fillColor: [173, 216, 230], textColor: [0,0,0], fontStyle: 'bold' },
        columnStyles: { 0: { cellWidth: 80 }, 1: { cellWidth: 20 }, 2: { cellWidth: 10 }, 3: { cellWidth: 60 } },
        margin: { top: 35, bottom: 25, left: 20, right: 20 }
      });
      y = doc.lastAutoTable.finalY + 10;
    }

    // --- BAGIAN: PSIKOGRAM ---
    if (opts.showPsychogram) {
      checkPageBreak(50);
      y += 5;
      
      const tableBody = [];
      rf.aspects_data.forEach((asp, aspIndex) => {
        const aspectLetter = String.fromCharCode(65 + aspIndex) + ". ";
        tableBody.push([{ content: aspectLetter + asp.name.toUpperCase(), colSpan: 7, styles: { fillColor: [240, 240, 240], fontStyle: 'bold' } }]);
        
        asp.sub_aspects.forEach((sub, index) => {
          tableBody.push([
            { content: `${index + 1}`, styles: { halign: 'center' } },
            { 
              content: `${sub.name}\n${sub.description || ''}`, 
              title: sub.name, 
              desc: sub.description || '', 
              isSubAspect: true 
            },
            { content: sub.category === 'R' ? 'X' : '', styles: { halign: 'center', valign: 'middle', fontStyle: 'bold' } }, 
            { content: sub.category === 'K' ? 'X' : '', styles: { halign: 'center', valign: 'middle', fontStyle: 'bold' } }, 
            { content: sub.category === 'C' ? 'X' : '', styles: { halign: 'center', valign: 'middle', fontStyle: 'bold' } }, 
            { content: sub.category === 'B' ? 'X' : '', styles: { halign: 'center', valign: 'middle', fontStyle: 'bold' } }, 
            { content: sub.category === 'T' ? 'X' : '', styles: { halign: 'center', valign: 'middle', fontStyle: 'bold' } }
          ]);
        });
      });

      autoTable(doc, {
        startY: y,
        head: [
          [{ content: getLetter() + 'ASPEK PSIKOLOGIS', colSpan: 2, rowSpan: 2, styles: { halign: 'center', valign: 'middle' } }, { content: 'KLASIFIKASI', colSpan: 5, styles: { halign: 'center' } }],
          ['R', 'K', 'C', 'B', 'T']
        ],
        body: tableBody,
        theme: 'grid',
        headStyles: { fillColor: [245, 245, 245], textColor: [0,0,0], fontStyle: 'bold', lineWidth: 0.1, lineColor: [200, 200, 200], halign: 'center', valign: 'middle' },
        columnStyles: { 
            0: { cellWidth: 10, halign: 'center' }, 
            1: { cellWidth: 80 }, 
            2: { cellWidth: 16 }, 3: { cellWidth: 16 }, 4: { cellWidth: 16 }, 5: { cellWidth: 16 }, 6: { cellWidth: 16 } 
        },
        margin: { top: 35, bottom: 25, left: 20, right: 20 },
        willDrawCell: function (data) {
          if (data.section === 'body' && data.column.index === 1 && data.cell.raw && data.cell.raw.isSubAspect) {
            // Kosongkan teks agar tidak digambar ganda oleh autoTable
            data.cell.text = [];
          }
        },
        didDrawCell: function (data) {
          if (data.section === 'body' && data.column.index === 1 && data.cell.raw && data.cell.raw.isSubAspect) {
            const textY = data.cell.y + data.cell.padding('top') + 3;
            
            // Cetak Title Bold
            doc.setFontSize(10);
            doc.setFont('helvetica', 'bold');
            doc.text(data.cell.raw.title, data.cell.x + data.cell.padding('left'), textY);
            
            // Cetak Deskripsi Normal
            if (data.cell.raw.desc) {
              doc.setFontSize(10);
              doc.setFont('helvetica', 'normal');
              const descLines = doc.splitTextToSize(data.cell.raw.desc, data.cell.width - data.cell.padding('left') - data.cell.padding('right'));
              doc.text(descLines, data.cell.x + data.cell.padding('left'), textY + 5);
            }
          }
        }
      });
      
      y = doc.lastAutoTable.finalY + 12;
    }

    // --- BAGIAN: DISC ---
    if (opts.showDISC && rf.disc_data) {
      checkPageBreak(60); 
      doc.setFontSize(11).setFont('helvetica', 'bold').text(getLetter() + "PROFIL KEPRIBADIAN (DISC)", 20, y);
      
      autoTable(doc, {
        startY: y + 5,
        head: [['', 'D', 'I', 'S', 'C', '*']],
        body: [
          ['Graph I (Public)', rf.disc_data.graph_1_most['D'], rf.disc_data.graph_1_most['I'], rf.disc_data.graph_1_most['S'], rf.disc_data.graph_1_most['C'], rf.disc_data.graph_1_most['*']],
          ['Graph II (Private)', rf.disc_data.graph_2_least['D'], rf.disc_data.graph_2_least['I'], rf.disc_data.graph_2_least['S'], rf.disc_data.graph_2_least['C'], rf.disc_data.graph_2_least['*']],
          ['Graph III (Mirror)', rf.disc_data.graph_3_change['D'], rf.disc_data.graph_3_change['I'], rf.disc_data.graph_3_change['S'], rf.disc_data.graph_3_change['C'], rf.disc_data.graph_3_change['*']],
        ],
        theme: 'grid',
        headStyles: { halign: 'center', fillColor: [52, 73, 94] },
        columnStyles: { 0: { fontStyle: 'bold' }, 1: { halign: 'center' }, 2: { halign: 'center' }, 3: { halign: 'center' }, 4: { halign: 'center' }, 5: { halign: 'center' } },
        margin: { top: 35, bottom: 25, left: 20, right: 20 }
      });

      // --- MENGGAMBAR 3 GRAFIK DISC ---
      let graphY = doc.lastAutoTable.finalY + 15;
      
      if (graphY + 90 > 275) {
        doc.addPage();
        graphY = 35;
      }
      
      const drawDiscGraph = (startX, title, subtitle, data, isChangeGraph) => {
        doc.setFontSize(9).setFont('helvetica', 'bold').setTextColor(0, 0, 0);
        doc.text(title, startX + 27.5, graphY, { align: 'center' });
        doc.setFontSize(8).setFont('helvetica', 'normal');
        doc.text(subtitle, startX + 27.5, graphY + 4, { align: 'center' });
        
        const boxY = graphY + 8;
        const boxW = 55;
        const boxH = 80;
        
        doc.setDrawColor(200, 200, 200).setLineWidth(0.2).rect(startX, boxY, boxW, boxH);
        doc.setFontSize(7).setTextColor(100, 100, 100);
        
        const maxVal = isChangeGraph ? 12 : 24;
        const minVal = isChangeGraph ? -12 : 0;
        const step = 4;
        
        for (let i = maxVal; i >= minVal; i -= step) {
          const yPos = boxY + ((maxVal - i) / (maxVal - minVal)) * boxH;
          doc.setDrawColor(200, 200, 200).setLineWidth(0.2);
          doc.line(startX, yPos, startX + boxW, yPos);
          doc.text(i.toString(), startX + 2, yPos - 1);
        }
        
        const zeroY = boxY + ((maxVal - 0) / (maxVal - minVal)) * boxH;
        doc.setDrawColor(150, 150, 150).setLineWidth(0.4);
        doc.line(startX, zeroY, startX + boxW, zeroY);
        
        const xLabels = ['D', 'I', 'S', 'C'];
        const xSpacing = boxW / 5;
        doc.setTextColor(0, 0, 0);
        xLabels.forEach((lbl, idx) => {
          const xPos = startX + xSpacing * (idx + 1);
          doc.text(lbl, xPos, zeroY + 4, { align: 'center' });
          doc.setDrawColor(0, 0, 0).setLineWidth(0.2);
          doc.line(xPos, zeroY - 1, xPos, zeroY + 1);
        });
        
        doc.setDrawColor(52, 152, 219).setFillColor(52, 152, 219).setLineWidth(0.6);
        let prevX = null;
        let prevY = null;
        
        xLabels.forEach((lbl, idx) => {
          let val = data[lbl];
          if (val === undefined || val === null) val = 0;
          val = Math.max(minVal, Math.min(maxVal, val));
          
          const px = startX + xSpacing * (idx + 1);
          const py = boxY + ((maxVal - val) / (maxVal - minVal)) * boxH;
          
          if (prevX !== null) doc.line(prevX, prevY, px, py);
          doc.circle(px, py, 1.0, 'FD');
          
          prevX = px;
          prevY = py;
        });
      };

      drawDiscGraph(15, "GRAPH 1 MOST", "Mask Public Self", rf.disc_data.graph_1_most, false);
      drawDiscGraph(77.5, "GRAPH 2 LEAST", "Core Private Self", rf.disc_data.graph_2_least, false);
      drawDiscGraph(140, "GRAPH 3 CHANGE", "Mirror Perceived Self", rf.disc_data.graph_3_change, true);
      
      y = graphY + 100;
    }

    // --- BAGIAN: PAPI KOSTICK ---
    if (opts.showPAPI && rf.papi_data) {
      checkPageBreak(50);
      
      doc.setFontSize(11).setFont('helvetica', 'bold').text(getLetter() + "PAPI KOSTICK", 20, y);
      y += 5;
      
      const papiKeys = Object.keys(rf.papi_data).sort();
      const papiValues = papiKeys.map(k => rf.papi_data[k]);
      
      autoTable(doc, {
        startY: y,
        head: [papiKeys],
        body: [papiValues],
        theme: 'grid',
        headStyles: { fillColor: [52, 73, 94], halign: 'center' },
        columnStyles: Object.fromEntries(papiKeys.map((_, i) => [i, { halign: 'center' }])),
        margin: { top: 35, bottom: 25, left: 20, right: 20 }
      });
      
      y = doc.lastAutoTable.finalY + 15;
    }

    // --- BAGIAN: MBTI (PREFERENSI KEPRIBADIAN) ---
    if (opts.showMBTI) {
      checkPageBreak(50);
      
      doc.setFontSize(11).setFont('helvetica', 'bold').text(getLetter() + "PREFERENSI KEPRIBADIAN", 20, y);
      y += 5;
      
      autoTable(doc, {
        startY: y,
        head: [[{ content: `Preferensi Kepribadian: ${rf.personality_type || '-'}`, colSpan: 2, styles: { halign: 'center' } }]],
        body: [[{ content: rf.personality_narrative, colSpan: 2 }]],
        theme: 'grid',
        headStyles: { fillColor: [169, 223, 191], textColor: [0,0,0], fontStyle: 'bold' }, // Light Green
        columnStyles: { 0: { cellWidth: 170 } },
        margin: { top: 35, bottom: 25, left: 20, right: 20 }
      });
      
      y = doc.lastAutoTable.finalY + 15;
    }

    // --- BAGIAN: SDS (MINAT KARIR) ---
    if (opts.showSDS) {
      const sdsScores = rf.sds_data;
      
      if (sdsScores || rf.career_interests) {
        checkPageBreak(50);
        doc.setFontSize(11).setFont('helvetica', 'bold').text(getLetter() + "MINAT KARIR (SDS)", 20, y);
        y += 5;
          
          // SDS Categories
          const sdsCategories = {
            'R': 'Realistic', 'I': 'Investigative',
            'A': 'Artistic', 'S': 'Social',
            'E': 'Enterprising', 'C': 'Conventional'
          };
          
          // Sort scores
          const sortedSds = Object.keys(sdsScores)
            .filter(k => ['R','I','A','S','E','C'].includes(k))
            .map(k => ({ code: k, name: sdsCategories[k] || k, score: sdsScores[k] }))
            .sort((a, b) => b.score - a.score);
            
          const top3 = sortedSds.slice(0, 3);
          
          const sdsBody = top3.map((item, index) => [
            { content: `Minat Karir ${index + 1}` },
            { content: item.name },
            { content: item.score, styles: { halign: 'center' } }
          ]);
          
          autoTable(doc, {
            startY: y,
            head: [['Peringkat', 'Kategori Minat Karir', 'Total Skor']],
            body: sdsBody.length ? sdsBody : [['-', 'Belum ada data', '-']],
            theme: 'grid',
            headStyles: { fillColor: [169, 223, 191], textColor: [0,0,0], fontStyle: 'bold', halign: 'center', valign: 'middle' },
            columnStyles: { 0: { cellWidth: 35, halign: 'center' }, 1: { cellWidth: 115 }, 2: { cellWidth: 20, halign: 'center' } },
            margin: { top: 35, bottom: 25, left: 20, right: 20 }
          });
          
          y = doc.lastAutoTable.finalY + 15;
      }
    }

    // --- BAGIAN: KESIMPULAN & REKOMENDASI ---
    if (opts.showConclusion) {
      checkPageBreak(50);
      doc.setFontSize(11).setFont('helvetica', 'bold').text(getLetter() + "KESIMPULAN", 20, y);
      y += 8;
      doc.setFontSize(10).text("1. Ringkasan Area Kekuatan dan Area Pengembangan", 20, y);
      y += 5;

      const splitToBullets = (text) => {
          if (!text) return "";
          return text.split('\n').filter(t => t.trim() !== '').map(t => {
              if (t.trim().startsWith('-') || t.trim().startsWith('•') || t.trim().startsWith('●')) {
                  return '- ' + t.trim().substring(1).trim();
              }
              return '- ' + t.trim();
          }).join('\n\n');
      };

      const strengthBullets = splitToBullets(rf.strengths_list);
      const weaknessBullets = splitToBullets(rf.weaknesses_list); 

      autoTable(doc, {
        startY: y,
        head: [['Area Kekuatan', 'Area Pengembangan']],
        body: [[strengthBullets, weaknessBullets]],
        theme: 'grid',
        headStyles: { fillColor: [169, 223, 191], textColor: [0,0,0], fontStyle: 'bold', halign: 'center' },
        columnStyles: { 0: { cellWidth: 85 }, 1: { cellWidth: 85 } },
        margin: { top: 35, bottom: 25, left: 20, right: 20 }
      });
      
      y = doc.lastAutoTable.finalY + 15;
      
      checkPageBreak(60);
      doc.setFontSize(10).setFont('helvetica', 'bold').text("Rekomendasi Hasil Asesment", 20, y);
      y += 5;

      autoTable(doc, {
        startY: y,
        head: [['Posisi\nYang\nDituju', 'Rentang\nKesesuaian (%)', 'Uraian']],
        body: [
          [rf.conclusion === 'Disarankan' ? 'X' : '', 'X >= 90%', 'Disarankan. Untuk aspek yang masih belum sesuai, diperlukan pengembangan pada aspek tersebut.'],
          [rf.conclusion === 'Dipertimbangkan' ? 'X' : '', '80% <= X < 90%', 'Dipertimbangkan. Memerlukan pengembangan yang intensif pada beberapa aspek yang masih belum memenuhi persyaratan.'],
          [rf.conclusion === 'Tidak Disarankan' ? 'X' : '', 'X < 80%', 'Tidak Disarankan']
        ],
        theme: 'grid',
        headStyles: { fillColor: [169, 223, 191], textColor: [0,0,0], fontStyle: 'bold', halign: 'center', valign: 'middle' },
        columnStyles: { 0: { cellWidth: 20, halign: 'center', valign: 'middle', fontStyle: 'bold' }, 1: { cellWidth: 35, halign: 'center', valign: 'middle' }, 2: { cellWidth: 115 } },
        margin: { top: 35, bottom: 25, left: 20, right: 20 }
      });
      
      y = doc.lastAutoTable.finalY + 15;
    }

    // --- TANDA TANGAN & QR ---
    checkPageBreak(60);
    
    doc.setFontSize(10).setFont('helvetica', 'normal');
    doc.text(`${a.city || 'Jakarta'}, ${formatDate(new Date(), true)}`, 20, y);
    doc.text("Psikolog,", 20, y + 5);
    
    if (a.signature_image_url) {
       try {
         const sigUrl = `${window.location.origin}${a.signature_image_url}`;
         const sigImg = await loadImageElement(sigUrl);
         const isJpeg = sigUrl.toLowerCase().match(/\.jpe?g$/);
         doc.addImage(sigImg, isJpeg ? 'JPEG' : 'PNG', 20, y + 8, 30, 15);
       } catch (e) {
         console.warn("Gagal memuat tanda tangan:", e);
       }
    }
    
    doc.setFont('helvetica', 'bold');
    const nameStr = a.full_name || "Nama Psikolog";
    doc.text(nameStr, 20, y + 27);
    doc.setLineWidth(0.3);
    doc.line(20, y + 28, 20 + doc.getTextWidth(nameStr), y + 28);
    
    doc.setFont('helvetica', 'bold').text(`No. SIPP: ${a.sipp_number || '-'}`, 20, y + 33);

    // QR Code di Kanan
    const qrUrl = `${window.location.origin}/verify?id=${p.test_number}`;
    const qrDataUri = await qr.toDataURL(qrUrl);
    doc.addImage(qrDataUri, 'PNG', 140, y + 5, 25, 25);
    doc.setFontSize(8).setFont('helvetica', 'normal').text("Verifikasi Laporan", 140, y + 35);

    // --- APPLY HEADER & FOOTER TO ALL PAGES ---
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      
      // Header (Logo & Kotak Rahasia)
      doc.addImage(logoImgElement, 'PNG', 15, 10, 45, 15);
      
      doc.setDrawColor(0, 0, 0);
      doc.setLineWidth(0.5);
      doc.rect(140, 12, 50, 10);
      doc.setFontSize(12).setFont('helvetica', 'normal').text("RAHASIA", 165, 19, { align: 'center' });
      
      // Footer (Page Number)
      doc.setFontSize(10).setFont('helvetica', 'normal');
      doc.text(`${i} / ${pageCount}`, 105, 287, { align: 'center' });
    }

    doc.save(`Laporan_${p.name}.pdf`);
  } catch (error) {
    console.error("PDF Generation Error:", error);
    alert(`Gagal mencetak PDF. Silakan periksa console browser. Detail: ${error.message}`);
  }
};

const formatDate = (dateString, onlyDate = false) => {
  if (!dateString) return '-';
  const d = new Date(dateString);
  return d.toLocaleDateString('id-ID', { 
    day: 'numeric', month: 'long', year: 'numeric',
    ...(onlyDate ? {} : { hour: '2-digit', minute: '2-digit' })
  });
};

onMounted(fetchData);
</script>

<style scoped>
.participant-detail { max-width: 1200px; margin: auto; padding: 20px; font-family: 'Inter', sans-serif; }
.card { background: white; border-radius: 12px; box-shadow: 0 4px 20px rgba(0,0,0,0.08); padding: 25px; margin-bottom: 25px; }
.page-header { display: flex; justify-content: space-between; align-items: center; border-left: 5px solid #3498db; flex-wrap: wrap; gap: 15px; }
.header-actions { display: flex; gap: 10px; flex-wrap: wrap; }
.badge-pkg { background: #e1f0fa; color: #3498db; padding: 4px 12px; border-radius: 20px; font-weight: bold; font-size: 0.8em; }
.badge-vip { background: #f3e5f5; color: #9c27b0; padding: 4px 12px; border-radius: 20px; font-weight: bold; font-size: 0.8em; margin-left: 10px; }
.details-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 20px; }
.detail-item.full { grid-column: span 4; }
.detail-item input { width: 100%; border: 1px solid #ddd; padding: 8px; border-radius: 6px; }
.section-title { display: flex; align-items: center; gap: 12px; margin-bottom: 20px; color: #2c3e50; border-bottom: 2px solid #eee; padding-bottom: 10px; }
.iq-config-row { display: flex; gap: 30px; margin-bottom: 20px; }
textarea { width: 100%; border: 1px solid #ddd; border-radius: 8px; padding: 15px; font-size: 1em; line-height: 1.6; transition: 0.3s; }
textarea:focus { border-color: #3498db; outline: none; box-shadow: 0 0 8px rgba(52, 152, 219, 0.2); }
.preview-table { width: 100%; border-collapse: collapse; }
.preview-table th, .preview-table td { border: 1px solid #eee; padding: 12px; text-align: left; }
.select-cat { padding: 4px; font-weight: bold; border-radius: 4px; border: 1px solid #ccc; }
.btn-action { padding: 12px 24px; border-radius: 8px; border: none; font-weight: bold; cursor: pointer; color: white; transition: 0.2s; }
.btn-action:hover { opacity: 0.9; transform: translateY(-2px); }
.primary { background: #3498db; }
.success { background: #27ae60; }
.danger { background: #e74c3c; }

.verification-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 10px;
}
.verification-table th {
  background: #f8f9fa;
  color: #333;
  text-transform: uppercase;
  font-size: 0.85em;
  letter-spacing: 0.5px;
}
.score-edit-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
}
.score-input {
  width: 70px;
  padding: 5px;
  border: 1px solid #ddd;
  border-radius: 4px;
  text-align: center;
  font-weight: bold;
}
.btn-save-mini {
  padding: 4px 8px;
  background: #27ae60;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 0.75em;
  cursor: pointer;
}
.btn-download-excel {
  background: #1d6f42; /* Warna khas Excel */
  color: white;
  border: none;
  padding: 6px 12px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.85em;
  display: inline-flex;
  align-items: center;
  gap: 5px;
}
.btn-download-excel:hover {
  background: #145a32;
}

.btn-koreksi-essay {
  padding: 4px 10px;
  background: #e67e22; /* Warna khas edit/warning */
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.85em;
  font-weight: 500;
  transition: background-color 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 5px;
}

.btn-koreksi-essay:hover {
  background: #d35400;
}

.btn-text-only { color: #999; }
.text-muted { color: #999; }
.hint {
  font-size: 0.9em;
  color: #666;
  margin-bottom: 15px;
  font-style: italic;
}
.disc-tables-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-top: 15px;
}
.disc-table h4 {
  margin-bottom: 10px;
  color: #2c3e50;
}
.text-center {
  text-align: center;
}
</style>